import * as moment from 'moment';
import * as angular from 'angular';

import {
  Inject,
  Injectable,
} from 'ng-metadata/core';

import { AuthService } from 'services/auth.service';
import { SearchService } from 'services/search.service';
import { ProductService } from 'services/product.service';
import { SessionService } from 'services/session.service';
import { EtomsService } from 'services/etoms.service';
import { constants } from 'http2';

/**
 * Shopping Cart Service
 *
 * This service takes care of common functionality
 * dealing with displaying products on cart and other
 * order processing formatting behavior.
 */
@Injectable('CartService')
export class CartService {
  public GW = (<any>window).GW;
  public cart = this.GW.cart;
  public geoip = this.GW.geoip;
  public remarketing = this.GW.remarketing;

  public emptyCart = {
    itinerary: {
      destinations: {},
      travelers: {
        adults: 1,
        children: 0,
      },
    },
    order: [],
    options: {},
    products: {},
    travelers: {},
    coupon: this.remarketing ? this.remarketing.coupon : null,
  };

  public itineraryDestination = {
    country: null,
    edit: true,
    dates: {
      startDate: null,
      endDate: null,
    },
  };

  public newProduct = {
    data: {},
    options: {
      entries: null,
      expediting: null,
    },
  };

  public newTraveler = {
    id: null,
    dob: null,
    firstName: null,
    lastName: null,
    friendlyName: null,
    citizenship: null,
    residency: null,
  };

  public entryOptionLabels = {
    single: 'Single Entry',
    double: 'Double Entry',
    multiple: 'Multiple Entry',
  };

  public etomsEnabled: Boolean = false;

  constructor(
    @Inject('$q') private $q: ng.IQService,
    @Inject('$log') private $log: ng.ILogService,
    @Inject('$http') private $http: ng.IHttpService,
    @Inject('$filter') private $filter,
    @Inject(AuthService) private authService: AuthService,
    @Inject(SearchService) private searchService: SearchService,
    @Inject(ProductService) private productService: ProductService,
    @Inject(SessionService) private sessionService: SessionService,
    @Inject(EtomsService) private etomsService: EtomsService,
  ) {
    this.etomsEnabled = this.etomsService.enabled();
  }

  /**
   * Get saved cart from session
   */
  getCart() {
    if (!this.cart) {
      this.cart = angular.copy(this.emptyCart);
    }

    if (!this.cart.coupon && this.remarketing.coupon) {
      this.cart.coupon = this.remarketing.coupon;
    }

    this.sanitizeCartJson();

    return this.cart;
  }

  /**
   * Empty objects are returned as empty arrays from server for some reason.
   * This is a dirty hack to make sure they are objects, or else everything breaks.
   * @todo Figure out how to fix this on the server side.
   */
  sanitizeCartJson() {
    if (Array.isArray(this.cart.travelers) && this.cart.travelers.length === 0) {
      this.cart.travelers = {};
    }
    if (Array.isArray(this.cart.options) && this.cart.options.length === 0) {
      this.cart.options = {};
    }
    if (Array.isArray(this.cart.products) && this.cart.products.length === 0) {
      this.cart.products = {};
    }
    if (Array.isArray(this.cart.itinerary.destinations) && this.cart.itinerary.destinations.length === 0) {
      this.cart.itinerary.destinations = {};
    }
  }

  /**
   * Remove a traveler and all associated order items
   * @param travelerId
   */
  removeTraveler(travelerId: string) {
    // Remove traveler
    delete this.cart.travelers[travelerId];

    // Delete all order items for this traveler
    this.cart.order.forEach((orderItem) => {
      if (orderItem.travelerId === travelerId) this.removeOrderItem(orderItem, true);
    });
  }

  /**
   * Add new product
   * @param product
   */
  addProduct(product: any) {
    this.cart.products[product.uuid] = product;
  }

  /**
   * Remove a product
   * @param productId
   */
  removeProduct(productId: string) {
    // Remove product
    delete this.cart.products[productId];

    // Delete all order items for this product
    this.cart.order.forEach((orderItem) => {
      if (orderItem.productId === productId) this.removeOrderItem(orderItem, true);
    });
  }

  /**
   * Add set of options
   * @param options
   */
  addOptions(options: any) {
    this.cart.options[options.uuid] = options;
  }

  /**
   * Remove order item
   */
  removeOrderItem(orderItem: any, bulk: boolean = false) {
    // Delete first instance of item, not all of them
    if (! bulk) {
      const index = this.cart.order.findIndex(item => angular.equals(item, orderItem));
      if (index > -1) this.cart.order.splice(index, 1);
    } else {
      this.cart.order = this.cart.order.filter(item => !angular.equals(item, orderItem));
    }

    this.cleanupOptions(orderItem.optionsId);
    this.cleanupProducts(orderItem.productId);
    this.cleanupTravelers(orderItem.travelerId);
    this.cleanupItinerary(orderItem.itineraryId);
  }

  /**
   * Removes a product if no longer present on order
   * @param productId string
   */
  cleanupProducts(productId: string) {
    const index = this.cart.order.findIndex(item => item.productId === productId);
    if (index < 0) delete this.cart.products[productId];
  }

  cleanupTravelers(travelerId: string) {
    const index = this.cart.order.findIndex(item => item.travelerId === travelerId);
    if (index < 0) delete this.cart.travelers[travelerId];
  }

  cleanupOptions(optionsId: string) {
    const index = this.cart.order.findIndex(item => item.optionsId === optionsId);
    if (index < 0) delete this.cart.options[optionsId];
  }

  cleanupItinerary(itineraryId: string) {
    const index = this.cart.order.findIndex(item => item.itineraryId === itineraryId);
    if (index < 0) delete this.cart.itinerary.destinations[itineraryId];
  }

  /**
   * Saves shopping cart state to session
   */
  async saveCart(cart?: any) {
    try {
      const response = await this.sessionService.saveState(cart || this.cart);
      return response;
    } catch (e) {
      throw e;
    }
  }

  /**
   * Resets session state
   */
  resetCart() {
    this.sessionService.resetState();
    this.cart = angular.copy(this.emptyCart);
    return this.cart;
  }

  getOutboundPrice(orderItem) {
    let outbound = 0;

    if (orderItem.type === 'visa') {
      outbound += this.cart.travelers[orderItem.travelerId].outbound.price;
    } else if (orderItem.outbound) {
      outbound += orderItem.outbound.price;
    }

    return outbound;
  }

  /**
   * Calculate shipping total from single order item
   * @param orderItem
   */
  getShippingTotal(orderItem) {
    if (!orderItem.requireShipping) {
      return 0
    }

    const option = this.cart.options[orderItem.optionsId];
    const inbound = option ? option.inboundShipping : 0;
    const outbound = orderItem.outbound ? orderItem.outbound.price : 0;

    return inbound + outbound;
  }

  getSubtotalNew(orderItem) {
    let subtotal = 0;

    if (!orderItem || !orderItem.productId) {
      return subtotal;
    }

    // Add shipping
    // subtotal += this.getShippingTotal(orderItem);

    // Add fees
    const options = this.cart.options[orderItem.optionsId];

    if (options) {
      if (orderItem.chargeEmbassyFee) {
        subtotal += options.embassyFee || 0;
      }

      subtotal += options.processingFee || 0;

      subtotal += this.getProcessingFeeDiscount(options.processingFee);
    }

    // Add chosen addon fees
    if (!this.etomsEnabled) {
      if (Array.isArray(orderItem.addons) && orderItem.addons.length) {
        orderItem.addons.forEach((addon) => {
          subtotal += addon.price;
        });
      }
    } else {
      orderItem.addons.forEach((addonId) => {
        if (this.cart.products.hasOwnProperty(orderItem.productId)) {
          const addon = this.cart.products[orderItem.productId].addons['list'][addonId];
          subtotal += Number(addon.value);
        }
      });
    }

    return subtotal;
  }

  /**
   * Calculate product subtotal by adding fees
   * and expediting cost.
   */
  getSubtotal(shipping: number, options, addons): number {
    let subtotal = 0;

    if (!options) {
      return subtotal;
    }

    // Add shipping
    subtotal += shipping || 0;
    subtotal += options ? options.inboundShipping : 0;

    // Add fees
    if (options.chargeEmbassyFee) {
      subtotal += options.embassyFee || 0;
    }

    subtotal += options.processingFee || 0;

    // Add chosen addon fees
    if (Array.isArray(addons) && addons.length) {
      addons.forEach((addon) => {
        subtotal += addon.price;
      });
    }

    return subtotal;
  }

  /**
   * Calculate and return order total
   */
  getOrderTotal(addonsData?): number {
    let total = 0;
    let shippingAdded = false;

    this.cart.order.forEach((item, index) => {
      total += this.getSubtotalNew(item);
      if (!this.etomsEnabled || !shippingAdded) {
        total += this.getShippingTotal(item);
        shippingAdded = item.requireShipping;
      }
    });

    return total;
  }

  // @todo Assumes it's only one traveler/product
  determineMailaway(product, option) {
    if (!product || !option) {
      return false;
    }

    if (product.subtype === 'passport-renewal' && option.mailaway) {
      return true;
    }

    return false;
  }

  determineGovFee(product, addons, option) {
    let fee = 190;

    // If no product is defined yet, show default
    if (!product) {
      return fee;
    }

    const isChildPassport = product.subtype.indexOf('child') > -1;

    if (isChildPassport) {
      fee = 160;
    }

    // Add fees for addons
    if (Array.isArray(addons) &&
      addons.find(addon => addon.label === 'Passport Card')) {
      fee += isChildPassport ? 15 : 30;
    }

    // Add mailaway fee
    if (this.determineMailaway(product, option)) {
      fee += 17.56;
    }

    return fee;
  }

  /**
   * Product processing options map function
   */
  mapVisaProcessingOptions(processing) {
    const entryOptionLabels = {
      single: 'Single Entry',
      double: 'Double Entry',
      multiple: 'Multiple Entry',
    };

    let unit: any;

    if (processing.validity[processing.validity.length - 1] === 'M') {
      unit = 'month';
    } else {
      unit = moment
        .normalizeUnits(processing.validity[processing.validity.length - 1]
        .toLowerCase());
    }

    const length: number = moment.duration(processing.validity).as(unit);
    const end: string = length > 1 ? 's' : '';
    const humanValidity: string =
      length + ' ' + unit[0].toUpperCase() + unit.slice(1, unit.length) + end;

    let label = this.productService.getDaysRange(
      processing.process_time_from,
      processing.process_time_to,
    );

    label += ' Business Days';

    return {
      label,
      uuid: processing.options_uuid,
      entries: processing.entry_type,
      embassyFee: processing.gov_fee / 100,
      inboundShipping: processing.shipping_cost / 100,
      processingFee: processing.process_fee / 100,
      entryLabel: entryOptionLabels[processing.entry_type],
      validity: humanValidity,
      chargeEmbassyFee: processing.charge_gov_fee == 0 ? false : true,
    };
  }

  /**
   * Update the itinerary start date
   * @param destinationId
   * @param startDate
   *
   * @return {}
   */
  async setDestinationStartDate(destinationId, startDate) {
    if (!this.cart.itinerary.destinations.hasOwnProperty(destinationId)) {
      return this.GW.cart;
    }

    // setting the departure date globally for each visa product
    for (const destination in this.cart.itinerary.destinations) {
      this.cart.itinerary.destinations[destination].dates = {
        startDate: startDate,
        endDate: null,
      };
    }
    await this.saveCart();

    return this.GW.cart;
  }

  /**
   * Return the first item order that requires shipping or the first one
   * if none requires shipping
   *
   * @return {}
   */
  getFirstOrderWithShipping() {
    const index = this.cart.order.findIndex(order => order.requireShipping);

    return index !== -1
      ? this.cart.order[index]
      : this.cart.order[0];
  }

  /**
   * Calculate the processingFee discount
   * @param processingFee
   */
  getProcessingFeeDiscount(processingFee) {
    if (!this.cart.coupon) {
      return 0;
    }

    if (this.cart.coupon.discount_unit === 'percentage') {
      return -1 * (processingFee * this.cart.coupon.discount_value / 100);
    }

    return -1 * this.cart.coupon.discount_value;
  }
}



// WEBPACK FOOTER //
// ./resources/assets/js/services/cart.service.ts